/**
 * Zapisane zmiany układu („Edytuj układ”) jako różnice względem layoutu wygenerowanego z formularza.
 *
 * Nie zapisujemy całego canvasu Fabric (zawierałby teksty i zdjęcie w base64 i przestałby pasować
 * po zmianie danych w formularzu) - tylko to, co użytkownik zmienił:
 *
 * {
 *   elements: { [elementId]: { left, top, scaleX, fontSize, fill, ... } },  // zmienione właściwości
 *   removed: [elementId, ...],                                               // usunięte elementy layoutu
 *   added: [{ id: 'added-…', kind: 'text', text, left, top, ... }],          // dodane napisy
 * }
 */

import { Textbox } from 'fabric';
import { describeTextStyles, applyTextStyles } from './textStyles';

export const ADDED_ID_PREFIX = 'added-';

const NUMBER_PROPS = ['left', 'top', 'scaleX', 'scaleY', 'angle', 'width', 'fontSize'];
// Pogrubienie, pochylenie i podkreślenie są w `textStyles` (textStyles.js) - także dla fragmentów napisu
const VALUE_PROPS = ['fontFamily', 'fill', 'textAlign', 'flipX', 'flipY'];

const canDescribeStyles = fabricObject => typeof fabricObject?.getSelectionStyles === 'function';

/** Stan elementu do porównań: właściwości układu, tekst i style napisu */
export function snapshotLayoutObject(fabricObject, textMap) {
    return {
        props: pickLayoutProps(fabricObject),
        text: typeof fabricObject.text === 'string' ? fabricObject.text : undefined,
        styles: canDescribeStyles(fabricObject) ? describeTextStyles(fabricObject, textMap) : undefined,
    };
}
const NUMBER_TOLERANCE = 0.01;

const roundNumber = value => Math.round(value * 100) / 100;

/** Właściwości obiektu, które użytkownik może zmienić w edytorze układu */
export function pickLayoutProps(fabricObject) {
    const props = {};
    NUMBER_PROPS.forEach((key) => {
        if (typeof fabricObject[key] === 'number') props[key] = roundNumber(fabricObject[key]);
    });
    VALUE_PROPS.forEach((key) => {
        const value = fabricObject[key];
        // fill bywa wzorem (Pattern) - takich elementów nie da się edytować, pomijamy
        if (typeof value === 'string' || typeof value === 'boolean') props[key] = value;
    });
    return props;
}

/** Tylko właściwości różniące się od stanu bazowego (null, gdy nic się nie zmieniło) */
export function diffLayoutProps(base, current) {
    const diff = {};
    Object.keys(current).forEach((key) => {
        const before = base[key];
        const after = current[key];
        const changed = typeof after === 'number' && typeof before === 'number'
            ? Math.abs(after - before) > NUMBER_TOLERANCE
            : after !== before;
        if (changed) diff[key] = after;
    });
    return Object.keys(diff).length > 0 ? diff : null;
}

export function applyLayoutProps(fabricObject, props, textMap) {
    if (!props) return;
    const { textStyles, ...layoutProps } = props;
    fabricObject.set(layoutProps);
    if (textStyles && canDescribeStyles(fabricObject)) applyTextStyles(fabricObject, textStyles, textMap);
    if (typeof fabricObject.initDimensions === 'function') fabricObject.initDimensions();
    fabricObject.setCoords();
}

export function createAddedText(definition, editable) {
    const { id, kind, text, textStyles, ...props } = definition;
    const textbox = new Textbox(text || '', {
        ...props,
        selectable: editable,
        editable,
    });
    textbox.elementId = id;
    textbox.elementLabel = 'Dodany napis';
    if (textStyles) applyTextStyles(textbox, textStyles, null);
    textbox.setCoords();
    return textbox;
}

export const isEmptyLayoutChanges = changes => (
    !changes
    || ((!changes.elements || Object.keys(changes.elements).length === 0)
        && (!changes.removed || changes.removed.length === 0)
        && (!changes.added || changes.added.length === 0))
);

/**
 * Zmiany układu odczytane z canvasu.
 * @param {fabric.Object[]} objects - obiekty na canvasie
 * @param {Map<string, {props: object, text?: string, styles?: object}>} baseSnapshots - stan elementów tuż po wygenerowaniu
 * @param {(elementId: string) => object|null} getTextMap - mapa tekstu napisu (textDSL/segments.js)
 * @returns {{ layoutChanges: object, changedTexts: object, unsavedImages: number }}
 */
export function collectLayoutChanges(objects, baseSnapshots, getTextMap = () => null) {
    const elements = {};
    const added = [];
    const changedTexts = {};
    const presentIds = new Set();
    let unsavedImages = 0;

    objects.forEach((fabricObject) => {
        const { elementId } = fabricObject;
        const base = elementId ? baseSnapshots.get(elementId) : null;

        if (base) {
            presentIds.add(elementId);
            let diff = diffLayoutProps(base.props, pickLayoutProps(fabricObject));
            // Style napisu zapisujemy w całości (opis jest bezwzględny), gdy różnią się od stanu bazowego
            if (base.styles && canDescribeStyles(fabricObject)) {
                const styles = describeTextStyles(fabricObject, getTextMap(elementId));
                if (JSON.stringify(styles) !== JSON.stringify(base.styles)) diff = { ...(diff || {}), textStyles: styles };
            }
            if (diff) elements[elementId] = diff;
            if (typeof base.text === 'string' && typeof fabricObject.text === 'string' && fabricObject.text !== base.text) {
                changedTexts[elementId] = fabricObject.text;
            }
            return;
        }

        if (fabricObject.type === 'textbox') {
            if (!elementId) {
                fabricObject.elementId = `${ADDED_ID_PREFIX}${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
            }
            added.push({
                id: fabricObject.elementId,
                kind: 'text',
                text: fabricObject.text,
                ...pickLayoutProps(fabricObject),
                ...(canDescribeStyles(fabricObject) ? { textStyles: describeTextStyles(fabricObject, null) } : {}),
            });
            return;
        }

        // Obrazki dodane w edytorze nie są jeszcze zapisywane (wymagają wysłania pliku na serwer)
        if (fabricObject.type === 'image') unsavedImages += 1;
    });

    const removed = [...baseSnapshots.keys()].filter(id => !presentIds.has(id));

    return {
        layoutChanges: { elements, removed, added },
        changedTexts,
        unsavedImages,
    };
}
